/** Structural subset of a TanStack Query result — keeps @loop3/ui free of a react-query dependency. */
export interface QueryLike {
  data: unknown
  isError: boolean
  refetch: () => unknown
}

export type CombinedQueryStatus = 'pending' | 'error' | 'ready'

/**
 * Reduces a page's queries to one status: 'error' only when an errored query
 * has nothing renderable (no cached `data`) — a background refetch failure
 * that left stale data on screen still counts as 'ready'; otherwise any
 * query still missing `data` is 'pending'; empty input is 'ready'. `retry`
 * refetches every errored query, including ones with cached data, and
 * resolves when they settle. Do not pass `enabled: false` queries — they
 * read as permanently 'pending'.
 */
export function combineQueryStates(queries: readonly QueryLike[]): {
  status: CombinedQueryStatus
  retry: () => Promise<void>
} {
  const errored = queries.filter((q) => q.isError)
  const status: CombinedQueryStatus =
    errored.some((q) => q.data === undefined)
      ? 'error'
      : queries.some((q) => q.data === undefined)
        ? 'pending'
        : 'ready'

  return {
    status,
    retry: async () => {
      await Promise.allSettled(errored.map((q) => q.refetch()))
    },
  }
}
